import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { parseAssetTagBarcode } from '../utils/assetTagBarcode';
import './BarcodeScanSection.css';

type ScanSource = 'camera' | 'ir';

/**
 * Feedback the parent can return from `onDecoded` while in batch mode so the
 * scanner can show an inline status banner ("Added", "Already scanned", etc.)
 * without interrupting the scanning flow.
 */
export type BatchScanFeedback = {
  kind: 'added' | 'duplicate' | 'invalid' | 'existing';
  /** Optional override message; otherwise a default per-kind message is used. */
  message?: string;
};

type BarcodeScanSectionProps = {
  /**
   * Called for every captured barcode. In single-scan mode this fires once
   * (and the camera stops). In batch mode it fires for every accepted decode
   * and the camera keeps scanning. The parent may return feedback so the
   * scanner can render an inline banner.
   */
  onDecoded: (text: string) => BatchScanFeedback | void;
  onBack: () => void;
  /** When set, scanner remounts (e.g. retry after invalid parse). */
  remountKey?: number;
  /**
   * When true, the scanner does NOT stop on a successful decode; it stays live
   * so the operator can scan many labels back-to-back. Same-text repeats inside
   * a short cooldown window are silently ignored to prevent the camera from
   * re-capturing the same label every frame.
   */
  batchMode?: boolean;
  /**
   * Optional content rendered after the scanner UI and before the debug log
   * (e.g. batch "Show items" so it sits above the collapsible debug panel).
   */
  toolbarBeforeLog?: React.ReactNode;
};

/** html5-qrcode throws/rejects if stop() runs when the scanner never started or is already stopped (e.g. React Strict Mode or double teardown). */
async function safeStopAndClear(qr: Html5Qrcode): Promise<void> {
  try {
    await qr.stop();
  } catch {
    /* not running / already stopped */
  }
  try {
    qr.clear();
  } catch {
    /* ignore */
  }
}

/** Remove any leftover nodes (avoids stacked video elements when Strict Mode or quick remounts run start() twice). */
function clearReaderDom(elementId: string): void {
  const el = document.getElementById(elementId);
  if (el) el.innerHTML = '';
}

/** html5-qrcode requires each qrbox side ≥ 50px (Constants.MIN_QR_BOX_SIZE). */
const MIN_QRBOX_PX = 50;

function computeQrboxSize(viewfinderWidth: number, viewfinderHeight: number): { width: number; height: number } {
  const w = viewfinderWidth > 0 ? viewfinderWidth : MIN_QRBOX_PX;
  const h = viewfinderHeight > 0 ? viewfinderHeight : MIN_QRBOX_PX;
  /** Target width, then enforce library minimum and never exceed viewfinder. */
  let width = Math.floor(w * 0.92);
  width = Math.max(MIN_QRBOX_PX, width);
  width = Math.min(width, w);
  let height = Math.min(Math.floor(h * 0.55), Math.floor(width * 0.45));
  height = Math.max(height, Math.min(160, h));
  height = Math.max(MIN_QRBOX_PX, height);
  height = Math.min(height, h);
  return { width, height };
}

const SCAN_SOURCE_STORAGE_KEY = 'barcodeScanSource';

function readPersistedScanSource(): ScanSource {
  try {
    const v = window.localStorage.getItem(SCAN_SOURCE_STORAGE_KEY);
    return v === 'ir' ? 'ir' : 'camera';
  } catch {
    return 'camera';
  }
}

function persistScanSource(source: ScanSource): void {
  try {
    window.localStorage.setItem(SCAN_SOURCE_STORAGE_KEY, source);
  } catch {
    /* private mode or storage disabled — non-fatal */
  }
}

/* ──────────────────────────────────────────────────────────────────────────
 * Live debug log
 * ────────────────────────────────────────────────────────────────────────── */

type LogLevel = 'info' | 'warn' | 'error' | 'debug';
type LogSource = 'cam' | 'ir' | 'ui';
type LogEntry = { id: number; ts: number; level: LogLevel; source: LogSource; message: string };
type LogFn = (level: LogLevel, source: LogSource, message: string) => void;

const MAX_LOG_ENTRIES = 120;

function useScanLog(): { entries: LogEntry[]; log: LogFn; clear: () => void } {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const idRef = useRef(0);
  const log = useCallback<LogFn>((level, source, message) => {
    const next: LogEntry = {
      id: ++idRef.current,
      ts: Date.now(),
      level,
      source,
      message,
    };
    setEntries((prev) => {
      const merged = [...prev, next];
      return merged.length > MAX_LOG_ENTRIES ? merged.slice(merged.length - MAX_LOG_ENTRIES) : merged;
    });
  }, []);
  const clear = useCallback(() => setEntries([]), []);
  return { entries, log, clear };
}

function formatTs(ts: number): string {
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const mmm = String(d.getMilliseconds()).padStart(3, '0');
  return `${hh}:${mm}:${ss}.${mmm}`;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Component
 * ────────────────────────────────────────────────────────────────────────── */

/** Cooldown for same-text repeats while batch-scanning (camera tends to re-decode every frame). */
const BATCH_REPEAT_COOLDOWN_MS = 1500;
/** How long the inline batch-status banner stays visible. */
const BATCH_BANNER_DURATION_MS = 3800;

type BatchBanner = {
  id: number;
  kind: BatchScanFeedback['kind'];
  text: string;
  message: string;
};

function defaultBatchMessage(kind: BatchScanFeedback['kind']): string {
  switch (kind) {
    case 'added':
      return 'Added to batch';
    case 'duplicate':
      return 'Already scanned in this batch';
    case 'existing':
      return 'Already in inventory';
    case 'invalid':
    default:
      return 'Could not parse this barcode';
  }
}

const BarcodeScanSection: React.FC<BarcodeScanSectionProps> = ({
  onDecoded,
  onBack,
  remountKey = 0,
  batchMode = false,
  toolbarBeforeLog,
}) => {
  const reactId = useId();
  const elementId = `barcode-reader-${reactId.replace(/:/g, '')}`;
  const wedgeInputId = `barcode-wedge-${reactId.replace(/:/g, '')}`;
  const wedgeInputRef = useRef<HTMLInputElement>(null);
  const onDecodedRef = useRef(onDecoded);
  onDecodedRef.current = onDecoded;
  const batchModeRef = useRef(batchMode);
  batchModeRef.current = batchMode;
  const [wedgeValue, setWedgeValue] = useState('');
  const finishedRef = useRef(false);
  /** Browser `setTimeout` returns a number; Node typings use `NodeJS.Timeout` — avoid mixing. */
  const advanceFormTimerRef = useRef<number | null>(null);
  const heartbeatTimerRef = useRef<number | null>(null);
  /** Per-text "last accepted at" map for batch-mode cooldown. */
  const batchLastAcceptedRef = useRef<Map<string, number>>(new Map());
  const batchBannerTimerRef = useRef<number | null>(null);
  const batchBannerIdRef = useRef(0);
  const [batchBanner, setBatchBanner] = useState<BatchBanner | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'scanning' | 'success'>('scanning');
  const [capturedText, setCapturedText] = useState('');
  const [scanSource, setScanSourceState] = useState<ScanSource>(() => readPersistedScanSource());
  const [debugOpen, setDebugOpen] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');

  const { entries: logEntries, log, clear: clearLog } = useScanLog();
  const logRef = useRef(log);
  logRef.current = log;

  const setScanSource = useCallback((next: ScanSource) => {
    setScanSourceState(next);
    persistScanSource(next);
    logRef.current('info', 'ui', `Scan source: ${next}`);
  }, []);

  /**
   * Send a decoded text to the parent in batch mode and surface the returned
   * feedback as an inline banner. Same-text repeats inside the cooldown
   * window are silently ignored. Default-feedback `'added'` is assumed when
   * the parent doesn't return anything (so quick prototyping still shows
   * confirmation), but real callers should always return explicit feedback.
   */
  const dispatchBatch = useCallback(
    (rawText: string) => {
      const text = rawText.trim();
      if (!text) return;
      const now = performance.now();
      const lastTs = batchLastAcceptedRef.current.get(text) ?? 0;
      if (now - lastTs < BATCH_REPEAT_COOLDOWN_MS) {
        logRef.current('debug', 'cam', `Batch cooldown skip: "${text}"`);
        return;
      }
      batchLastAcceptedRef.current.set(text, now);
      /** Cleanup old map entries opportunistically so it never grows unbounded. */
      if (batchLastAcceptedRef.current.size > 200) {
        const cutoff = now - 60_000;
        batchLastAcceptedRef.current.forEach((v, k) => {
          if (v < cutoff) batchLastAcceptedRef.current.delete(k);
        });
      }

      const feedback = onDecodedRef.current(text) ?? { kind: 'added' as const };
      const message = feedback.message ?? defaultBatchMessage(feedback.kind);
      const id = ++batchBannerIdRef.current;
      logRef.current('info', batchModeRef.current ? 'ir' : 'cam', `Batch ${feedback.kind}: "${text}"`);
      setBatchBanner({ id, kind: feedback.kind, text, message });
      if (batchBannerTimerRef.current) {
        clearTimeout(batchBannerTimerRef.current);
      }
      batchBannerTimerRef.current = window.setTimeout(() => {
        batchBannerTimerRef.current = null;
        setBatchBanner((current) => (current && current.id === id ? null : current));
      }, BATCH_BANNER_DURATION_MS);
    },
    []
  );

  /** Reset cooldown + banner whenever the parent toggles batch mode or remounts. */
  useEffect(() => {
    batchLastAcceptedRef.current.clear();
    setBatchBanner(null);
    if (batchBannerTimerRef.current) {
      clearTimeout(batchBannerTimerRef.current);
      batchBannerTimerRef.current = null;
    }
  }, [batchMode, remountKey]);

  /** Clean up the banner timer on unmount so it can't fire on a stale state setter. */
  useEffect(() => {
    return () => {
      if (batchBannerTimerRef.current) {
        clearTimeout(batchBannerTimerRef.current);
        batchBannerTimerRef.current = null;
      }
    };
  }, []);

  /* ──────────────────────────────────────────────────────────────────
   * Camera path — restored to the previous working configuration so
   * we have a reliable baseline. The only addition here is logging:
   * lifecycle (start/stop/error), every parsed/spurious decode, and a
   * 2-second heartbeat showing decode-attempt and ignored-decode counts.
   * ────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (scanSource !== 'camera') {
      return () => {};
    }

    let cancelled = false;
    finishedRef.current = false;
    setCameraError(null);
    setPhase('scanning');
    setCapturedText('');
    if (advanceFormTimerRef.current) {
      clearTimeout(advanceFormTimerRef.current);
      advanceFormTimerRef.current = null;
    }
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }

    clearReaderDom(elementId);

    /** Counters shared with heartbeat + decode callbacks. */
    const counters = { decodeFails: 0, decodeTexts: 0, parseFails: 0, parsed: 0 };

    logRef.current('info', 'cam', 'Initializing Html5Qrcode (Code 128 only, BarcodeDetector off)');

    const html5QrCode = new Html5Qrcode(elementId, {
      verbose: false,
      formatsToSupport: [Html5QrcodeSupportedFormats.CODE_128],
      /**
       * Use html5-qrcode’s decoder only. Native BarcodeDetector on Android can return
       * inconsistent or non–Code-128 reads on successive scans; ignoring those is harder
       * before stop(), so we prefer a single pipeline.
       */
      useBarCodeDetectorIfSupported: false,
    });

    void (async () => {
      try {
        /**
         * Mobile-first: rear camera (`environment`) for scanning labels. `videoConstraints` drives
         * the stream (ideal HD when the device supports it).
         */
        await html5QrCode.start(
          { facingMode: 'environment' },
          {
            fps: 24,
            /**
             * Large region: small fixed qrbox starves Code 128 of pixels (especially on screen-in-screen).
             * Use ~90% × ~50% of view so the barcode fills the scan canvas.
             */
            qrbox: (vw, vh) => computeQrboxSize(vw, vh),
            aspectRatio: 1.777777778,
            videoConstraints: {
              facingMode: 'environment',
              width: { ideal: 1920, min: 720 },
              height: { ideal: 1080, min: 720 },
            },
          },
          (decodedText) => {
            if (cancelled) return;
            counters.decodeTexts += 1;
            const parsed = parseAssetTagBarcode(decodedText);
            if (!parsed) {
              counters.parseFails += 1;
              const preview = decodedText.length > 60 ? `${decodedText.slice(0, 60)}…` : decodedText;
              logRef.current('warn', 'cam', `Decoded but did not match asset-tag pattern: "${preview}"`);
              /** Spurious reads (e.g. numeric noise) should not stop the camera or surface as errors. */
              return;
            }
            counters.parsed += 1;
            logRef.current('info', 'cam', `Decoded asset tag: "${decodedText}"`);

            if (batchModeRef.current) {
              /** Batch: camera stays live; dispatchBatch handles cooldown + banner. */
              dispatchBatch(decodedText);
              return;
            }

            if (finishedRef.current) return;
            finishedRef.current = true;
            void safeStopAndClear(html5QrCode).then(() => {
              if (cancelled) return;
              setCapturedText(decodedText);
              setPhase('success');
              advanceFormTimerRef.current = window.setTimeout(() => {
                advanceFormTimerRef.current = null;
                if (!cancelled) onDecodedRef.current(decodedText);
              }, 500);
            });
          },
          () => {
            counters.decodeFails += 1;
          }
        );
        if (cancelled) return;
        const settings = (() => {
          try {
            return html5QrCode.getRunningTrackSettings();
          } catch {
            return null;
          }
        })();
        const dim = settings && (settings as MediaTrackSettings).width && (settings as MediaTrackSettings).height
          ? `${(settings as MediaTrackSettings).width}×${(settings as MediaTrackSettings).height}`
          : 'unknown';
        const facing = (settings as MediaTrackSettings | null)?.facingMode ?? 'unknown';
        logRef.current('info', 'cam', `Camera started — ${dim}, facing=${facing}`);

        /** Heartbeat so the user can see decode activity even when nothing parses. */
        heartbeatTimerRef.current = window.setInterval(() => {
          if (cancelled) return;
          logRef.current(
            'debug',
            'cam',
            `Heartbeat — frames w/o decode: ${counters.decodeFails}, decoded texts: ${counters.decodeTexts} (parse-fail ${counters.parseFails}, ok ${counters.parsed})`
          );
          counters.decodeFails = 0;
          counters.decodeTexts = 0;
          counters.parseFails = 0;
          counters.parsed = 0;
        }, 2000);
      } catch (err: unknown) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setCameraError(msg || 'Could not start camera.');
        finishedRef.current = true;
        logRef.current('error', 'cam', `Camera failed to start: ${msg}`);
      }
    })();

    return () => {
      cancelled = true;
      finishedRef.current = true;
      if (advanceFormTimerRef.current) {
        clearTimeout(advanceFormTimerRef.current);
        advanceFormTimerRef.current = null;
      }
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      logRef.current('info', 'cam', 'Stopping camera');
      void safeStopAndClear(html5QrCode).finally(() => {
        clearReaderDom(elementId);
      });
    };
  }, [elementId, remountKey, scanSource]);

  useEffect(() => {
    setWedgeValue('');
  }, [remountKey]);

  /** When using the rear camera, keep the wedge field unfocused so HID scan data is not merged with the camera path. */
  useEffect(() => {
    if (scanSource !== 'camera') return;
    const id = window.requestAnimationFrame(() => {
      wedgeInputRef.current?.blur();
    });
    return () => window.cancelAnimationFrame(id);
  }, [remountKey, scanSource]);

  /** IR / keyboard-wedge mode: focus the capture field so the device scanner sends data here. */
  useEffect(() => {
    if (scanSource !== 'ir') return;
    const t = window.setTimeout(() => {
      wedgeInputRef.current?.focus();
      logRef.current('info', 'ir', 'Wedge input focused');
    }, 150);
    return () => window.clearTimeout(t);
  }, [remountKey, scanSource]);

  /**
   * Passive diagnostics in IR mode: log every keydown the page sees plus blur
   * events on our input. We do NOT preventDefault or buffer — this is purely
   * to tell us whether the Honeywell trigger is delivering keyboard events at
   * all, on what target, and with what cadence. If we discover (for example)
   * that bursts arrive while focus is on a button, we can act on that.
   */
  useEffect(() => {
    if (scanSource !== 'ir') return;
    let lastKeyTs = 0;
    let burstCount = 0;
    let burstStart = 0;

    const handleKey = (e: KeyboardEvent) => {
      const now = performance.now();
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName ?? 'window';
      const isOurInput = target === wedgeInputRef.current;
      const where = isOurInput ? 'wedge-input' : tag.toLowerCase();
      const gap = lastKeyTs ? Math.round(now - lastKeyTs) : 0;

      if (!lastKeyTs || now - lastKeyTs > 200) {
        burstStart = now;
        burstCount = 1;
      } else {
        burstCount += 1;
      }
      lastKeyTs = now;

      const key = e.key.length === 1 ? e.key : `<${e.key}>`;
      logRef.current('debug', 'ir', `keydown ${key} on ${where} (+${gap}ms)`);

      if (e.key === 'Enter' || e.key === 'Tab') {
        const elapsed = Math.round(now - burstStart);
        logRef.current(
          'info',
          'ir',
          `Terminator received after burst of ${burstCount} keys over ${elapsed}ms on ${where}`
        );
        burstCount = 0;
        burstStart = 0;
      }
    };

    const input = wedgeInputRef.current;
    const handleBlur = () => logRef.current('warn', 'ir', 'Wedge input lost focus');
    const handleFocus = () => logRef.current('info', 'ir', 'Wedge input gained focus');

    window.addEventListener('keydown', handleKey, true);
    if (input) {
      input.addEventListener('blur', handleBlur);
      input.addEventListener('focus', handleFocus);
    }
    return () => {
      window.removeEventListener('keydown', handleKey, true);
      if (input) {
        input.removeEventListener('blur', handleBlur);
        input.removeEventListener('focus', handleFocus);
      }
    };
  }, [scanSource, remountKey]);

  /**
   * Submit the wedge value on Enter / "Use code". We read from the input ref
   * (not React state) because HID scanners can deliver characters faster than
   * React renders, leaving the controlled `wedgeValue` stale. The input's own
   * DOM value always reflects every keystroke the browser has processed.
   *
   * In batch mode the input is cleared and refocused after each submit so the
   * next trigger pull starts from a clean slate without unmounting.
   */
  const submitWedge = useCallback(() => {
    const fromInput = wedgeInputRef.current?.value ?? '';
    const text = (fromInput || wedgeValue).trim();
    logRef.current('info', 'ir', `Submitting wedge value (${text.length} chars): "${text}"`);
    if (!text) return;
    if (batchModeRef.current) {
      setWedgeValue('');
      if (wedgeInputRef.current) {
        wedgeInputRef.current.value = '';
        wedgeInputRef.current.focus();
      }
      dispatchBatch(text);
      return;
    }
    onDecoded(text);
  }, [wedgeValue, onDecoded, dispatchBatch]);

  /* ──────────────────────────────────────────────────────────────────
   * Debug panel actions
   * ────────────────────────────────────────────────────────────────── */

  const logText = useMemo(
    () =>
      logEntries
        .map((e) => `${formatTs(e.ts)} [${e.level.toUpperCase()}] ${e.source}: ${e.message}`)
        .join('\n'),
    [logEntries]
  );

  const handleCopyLog = useCallback(async () => {
    const text = logText || '(log is empty)';
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 1500);
    } catch {
      setCopyState('idle');
    }
  }, [logText]);

  return (
    <div className="barcode-scan-section">
      <div className="barcode-scan-actions barcode-scan-actions--top">
        <button type="button" className="cancel-button" onClick={onBack}>
          Back
        </button>
      </div>
      {cameraError && scanSource === 'camera' && (
        <p className="barcode-scan-error" role="alert">
          {cameraError}
        </p>
      )}
      {scanSource === 'camera' ? (
        <div className="barcode-scan-viewport-wrap">
          <div id={elementId} className="barcode-scan-viewport" />
          {batchMode && batchBanner && (
            <div
              key={batchBanner.id}
              className={`barcode-scan-batch-banner barcode-scan-batch-banner--${batchBanner.kind}`}
              role="status"
              aria-live="polite"
            >
              <span className="barcode-scan-batch-banner-msg">{batchBanner.message}</span>
              <span className="barcode-scan-batch-banner-code">{batchBanner.text}</span>
            </div>
          )}
          {!batchMode && phase === 'success' && (
            <div className="barcode-scan-success-overlay" role="status" aria-live="polite">
              <div className="barcode-scan-success-inner">
                <span className="barcode-scan-success-icon" aria-hidden>
                  ✓
                </span>
                <p className="barcode-scan-success-title">Barcode captured</p>
                <p className="barcode-scan-success-code">{capturedText}</p>
                <p className="barcode-scan-success-sub">Opening form…</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="barcode-scan-ir-panel" aria-label="Device scanner">
          <p className="barcode-scan-ir-title">Built-in scanner</p>
          <p className="barcode-scan-ir-body">
            {batchMode
              ? 'Pull the trigger to add labels to the batch. The field stays focused so each scan auto-records.'
              : 'This field stays focused so scan data from your device (e.g. Honeywell trigger) is captured. Pull the trigger to scan the Code 128 label, or paste the code and press '}
            {!batchMode && <kbd>Enter</kbd>}
            {!batchMode && '.'}
          </p>
          {batchMode && batchBanner && (
            <div
              key={batchBanner.id}
              className={`barcode-scan-batch-banner barcode-scan-batch-banner--${batchBanner.kind} barcode-scan-batch-banner--inline`}
              role="status"
              aria-live="polite"
            >
              <span className="barcode-scan-batch-banner-msg">{batchBanner.message}</span>
              <span className="barcode-scan-batch-banner-code">{batchBanner.text}</span>
            </div>
          )}
        </div>
      )}

      <div
        className="barcode-scan-source-toggle"
        role="group"
        aria-label="Scan source"
      >
        <button
          type="button"
          className={`barcode-scan-source-option${scanSource === 'camera' ? ' barcode-scan-source-option--active' : ''}`}
          onClick={() => setScanSource('camera')}
          aria-pressed={scanSource === 'camera'}
        >
          Rear camera
        </button>
        <button
          type="button"
          className={`barcode-scan-source-option${scanSource === 'ir' ? ' barcode-scan-source-option--active' : ''}`}
          onClick={() => setScanSource('ir')}
          aria-pressed={scanSource === 'ir'}
        >
          IR scanner
        </button>
      </div>

      {scanSource === 'ir' && (
        <div className="barcode-scan-wedge">
          <div className="barcode-scan-wedge-row">
            <input
              ref={wedgeInputRef}
              id={wedgeInputId}
              type="text"
              className="form-input barcode-scan-wedge-input"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="USA-ProductType-1234567890"
              value={wedgeValue}
              onChange={(e) => setWedgeValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitWedge();
                }
              }}
            />
            {!batchMode && (
              <button type="button" className="update-button barcode-scan-wedge-submit" onClick={submitWedge}>
                Use code
              </button>
            )}
          </div>
        </div>
      )}

      {toolbarBeforeLog ? (
        <div className="barcode-scan-toolbar-before-log">{toolbarBeforeLog}</div>
      ) : null}

      <DebugLogPanel
        open={debugOpen}
        entries={logEntries}
        onToggle={() => setDebugOpen((v) => !v)}
        onClear={clearLog}
        onCopy={handleCopyLog}
        copyState={copyState}
      />
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
 * Debug panel
 * ────────────────────────────────────────────────────────────────────────── */

type DebugLogPanelProps = {
  open: boolean;
  entries: LogEntry[];
  onToggle: () => void;
  onClear: () => void;
  onCopy: () => void;
  copyState: 'idle' | 'copied';
};

const DebugLogPanel: React.FC<DebugLogPanelProps> = ({
  open,
  entries,
  onToggle,
  onClear,
  onCopy,
  copyState,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  /** Auto-scroll to newest entry while panel is open. */
  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [open, entries]);

  const lastEntry = entries[entries.length - 1];

  return (
    <div className={`barcode-scan-debug${open ? ' barcode-scan-debug--open' : ''}`}>
      <button
        type="button"
        className="barcode-scan-debug-header"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="barcode-scan-debug-title">
          Debug log{' '}
          <span className="barcode-scan-debug-count">({entries.length})</span>
        </span>
        {!open && lastEntry && (
          <span className={`barcode-scan-debug-tail barcode-scan-debug-tail--${lastEntry.level}`}>
            {lastEntry.source}: {lastEntry.message}
          </span>
        )}
        <span className="barcode-scan-debug-arrow" aria-hidden>
          {open ? '▾' : '▸'}
        </span>
      </button>
      {open && (
        <>
          <div className="barcode-scan-debug-actions">
            <button type="button" className="barcode-scan-debug-btn" onClick={onCopy}>
              {copyState === 'copied' ? 'Copied!' : 'Copy log'}
            </button>
            <button type="button" className="barcode-scan-debug-btn" onClick={onClear}>
              Clear
            </button>
          </div>
          <div className="barcode-scan-debug-list" ref={listRef} role="log" aria-live="polite">
            {entries.length === 0 ? (
              <p className="barcode-scan-debug-empty">No events yet. Try scanning.</p>
            ) : (
              entries.map((e) => (
                <div key={e.id} className={`barcode-scan-debug-row barcode-scan-debug-row--${e.level}`}>
                  <span className="barcode-scan-debug-ts">{formatTs(e.ts)}</span>
                  <span className="barcode-scan-debug-src">{e.source}</span>
                  <span className="barcode-scan-debug-msg">{e.message}</span>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BarcodeScanSection;
